import {
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kalos/ui';
import { CrossCircledIcon } from '@radix-ui/react-icons';
import type { DispatchableTech } from 'kalos-rpc/src/api/compiled-protos/dispatch';
import { Clock, MapPin } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { JOB_STATUS_COLORS } from '../../../../constants';

export interface TechnicianAssignmentProps {
  technicians: DispatchableTech[];
  selectedTechnicianIds: number[];
  onSelectionChange: (technicianIds: number[]) => void;
  departmentId?: number;
  disabled?: boolean;
}

const getStatusStyle = (activity?: string) => {
  const baseClasses =
    'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-semibold w-fit';
  let icon: React.ReactNode = null;
  let backgroundColor = '';
  let textColor = 'text-black';
  let borderClasses = '';

  const status = (activity || '').toLowerCase();

  switch (status) {
    case 'standby':
      backgroundColor = 'bg-green-50';
      textColor = 'text-green-700';
      borderClasses = 'border border-green-100';
      break;
    case 'enroute':
      icon = <MapPin className="h-3 w-3" />;
      backgroundColor = JOB_STATUS_COLORS['Enroute'];
      textColor = 'text-black';
      borderClasses = 'border border-gray-200';
      break;
    case 'on call':
      backgroundColor = JOB_STATUS_COLORS['On Call'];
      textColor = 'text-black';
      borderClasses = 'border border-gray-200';
      break;
    case 'pto':
      icon = <Clock className="h-3 w-3" />;
      backgroundColor = 'bg-purple-50';
      textColor = 'text-purple-700';
      borderClasses = 'border border-purple-200';
      break;
    case 'dismissed':
      icon = <CrossCircledIcon className="h-3 w-3" />;
      backgroundColor = 'bg-gray-100';
      textColor = 'text-gray-600';
      borderClasses = 'border border-gray-200';
      break;
    default:
      backgroundColor = 'bg-gray-50';
      textColor = 'text-gray-700';
      borderClasses = 'border border-gray-200';
      break;
  }

  const isHexColor = !backgroundColor.startsWith('bg');

  return {
    className: `${baseClasses} ${isHexColor ? '' : backgroundColor} ${textColor} ${borderClasses}`,
    icon,
    label: activity || 'Unknown',
    backgroundColor: isHexColor ? `#${backgroundColor}` : '',
  };
};

const getInitials = (tech: DispatchableTech) => {
  const techname = tech.techname || '';

  if (techname) {
    const nameParts = techname.split(' ');
    return nameParts.length >= 2
      ? `${nameParts[0][0] ?? ''}${nameParts[nameParts.length - 1][0] ?? ''}`.toUpperCase()
      : techname.slice(0, 2).toUpperCase();
  }

  return '??';
};

const getTechnicianName = (tech: DispatchableTech) => {
  return tech.techname || 'Unknown';
};

export const TechnicianAssignment: FC<TechnicianAssignmentProps> = ({
  technicians,
  selectedTechnicianIds,
  onSelectionChange,
  departmentId,
  disabled = false,
}) => {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const departmentFilteredTechnicians = useMemo(() => {
    if (!departmentId) return technicians;
    return technicians.filter((tech) => tech.departmentCode === departmentId);
  }, [technicians, departmentId]);

  const statusCounts = useMemo(() => {
    const counts = {
      available: 0,
      busy: 0,
      enroute: 0,
    };

    departmentFilteredTechnicians.forEach((tech) => {
      const activity = tech.activity?.toLowerCase() || '';
      if (activity === 'standby' || !activity) {
        counts.available++;
      } else if (activity === 'on call') {
        counts.busy++;
      } else if (activity === 'enroute') {
        counts.enroute++;
      }
    });

    return counts;
  }, [departmentFilteredTechnicians]);

  const filteredTechnicians = useMemo(() => {
    let filtered = departmentFilteredTechnicians;

    if (filterStatus !== 'all') {
      filtered = filtered.filter((tech) => {
        const activity = tech.activity?.toLowerCase() || '';
        switch (filterStatus) {
          case 'available':
            return activity === 'standby' || !activity;
          case 'busy':
            return activity === 'on call';
          case 'enroute':
            return activity === 'enroute';
          default:
            return true;
        }
      });
    }

    if (search.trim()) {
      const searchLower = search.trim().toLowerCase();
      filtered = filtered.filter((tech) => {
        const name = getTechnicianName(tech).toLowerCase();
        return name.includes(searchLower);
      });
    }

    return filtered.sort((a, b) => {
      const aSelected = selectedTechnicianIds.includes(a.userId);
      const bSelected = selectedTechnicianIds.includes(b.userId);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return getTechnicianName(a).localeCompare(getTechnicianName(b));
    });
  }, [departmentFilteredTechnicians, filterStatus, search, selectedTechnicianIds]);

  // Reset scroll position when search or filter changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [search, filterStatus]);

  const handleToggle = useCallback(
    (techId: number) => {
      if (disabled) return;

      if (selectedTechnicianIds.includes(techId)) {
        onSelectionChange(selectedTechnicianIds.filter((id) => id !== techId));
      } else {
        onSelectionChange([...selectedTechnicianIds, techId]);
      }
    },
    [disabled, selectedTechnicianIds, onSelectionChange],
  );

  const handleAutoAssign = useCallback(() => {
    if (disabled) return;

    const availableTechs = departmentFilteredTechnicians.filter((tech) => {
      const activity = tech.activity?.toLowerCase() || '';
      return activity === 'standby' || !activity;
    });

    const toAssign = availableTechs.slice(0, 2).map((tech) => tech.userId);
    onSelectionChange(toAssign);
  }, [disabled, departmentFilteredTechnicians, onSelectionChange]);

  return (
    <div className="flex h-full w-full flex-col">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-green-500" />
            <span>Available: {statusCounts.available}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-red-500" />
            <span>Busy: {statusCounts.busy}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-orange-500" />
            <span>En Route: {statusCounts.enroute}</span>
          </div>
        </div>
        <div className="text-xs text-gray-600">
          {selectedTechnicianIds.length}/{departmentFilteredTechnicians.length}
        </div>
      </div>

      <div className="mb-2 flex gap-2">
        <Select value={filterStatus} onValueChange={setFilterStatus} disabled={disabled}>
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent position="popper" className="z-50">
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="busy">Busy</SelectItem>
            <SelectItem value="enroute">En Route</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
          className="flex-1"
        />
      </div>

      <div
        className="relative mb-2 h-[180px] overflow-y-auto rounded border border-gray-200 bg-gray-50 p-2"
        ref={scrollContainerRef}
      >
        {filteredTechnicians.length === 0 ? (
          <div className="py-4 text-center text-sm text-gray-500">No technicians found</div>
        ) : (
          filteredTechnicians.map((tech) => {
            const techId = tech.userId;
            const isSelected = selectedTechnicianIds.includes(techId);
            const isDismissed = (tech.activity || '').toLowerCase() === 'dismissed';
            const isItemDisabled = disabled || isDismissed;
            const { className, icon, label, backgroundColor } = getStatusStyle(tech.activity);
            return (
              <label
                key={techId}
                className={`mb-1.5 flex items-center justify-between rounded border bg-white p-2 last:mb-0 ${
                  isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                } ${isItemDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-50'}`}
              >
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={isSelected}
                    disabled={isItemDisabled}
                    className="rounded-[2px]"
                    onCheckedChange={() => {
                      if (!isItemDisabled) {
                        handleToggle(techId);
                      }
                    }}
                  />
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-bold">
                    {getInitials(tech)}
                  </div>
                  <span className="text-sm">{getTechnicianName(tech)}</span>
                </div>
                <span
                  className={className}
                  style={backgroundColor ? { backgroundColor } : undefined}
                >
                  {icon}
                  {label}
                </span>
              </label>
            );
          })
        )}
      </div>

      {statusCounts.available > 0 && (
        <button
          type="button"
          onClick={handleAutoAssign}
          disabled={disabled}
          className="w-full rounded border-2 border-dashed border-gray-300 py-1.5 text-sm text-gray-600 hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50"
        >
          Auto-assign {Math.min(statusCounts.available, 2)} Technician
          {Math.min(statusCounts.available, 2) !== 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
};
